import { t } from '@lingui/macro';
import { Headphones, Maximize2, Mic, MicOff, Minimize2, Minus, MonitorUp, PictureInPicture2, Plus, RotateCcw, Users, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { User } from '../../models/User';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { twil } from '../../services/TwilioHelper';
import { UtilApp } from '../../services/Utils/UtilApp';
import { UserAvatar } from './UserAvatar';
import UserPopOver from './UserPopover';

export const ParticipantItem: React.FC<{
  html: HTMLElement;
  /** 프로필이 없는 참가자(통화 링크 게스트 등)의 표시 이름 — 주면 users 조회를 건너뛴다 */
  displayName?: string;
  /** 이름 아래 보조 표기 (예: 통화 언어) */
  subLabel?: string;
  /** 외부에서 알려주는 음소거 상태 (통화 링크는 twil 이벤트 대신 코어가 소스) */
  mutedOverride?: boolean;
}> = ({ html, displayName, subLabel, mutedOverride }) => {
  const htmlElement = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<User>();
  const [isMuted, setIsMuted] = useState<boolean>();
  const [isDominant, setIsDominant] = useState<boolean>();
  const isLocalUser = html.id == My.user.id;

  useEffect(() => {

    if (displayName) {
      // 프로필 없는 참가자 — 조회를 건너뛰고 전달받은 이름으로 그린다.
      // (통화 링크의 identity 는 host-/guest- 접두 문자열이라 users 문서가 없다)
      setTimeout(() => { htmlElement.current?.appendChild(html); }, 100);
    } else if (isLocalUser) {
      setUser(My.user)
    } else {
      Fire.getUser(html.id)
        .then(u => {
          setUser(u);
          let remotePart = twil.remoteParticipants.get(html.id);

          if (remotePart) {
            let enabledAudioTrack = Array.from(remotePart?.audioTracks.values() ?? []).find(track => track.isTrackEnabled);
            setIsMuted(!enabledAudioTrack);
            setIsDominant(enabledAudioTrack && twil.twilioRoom?.dominantSpeaker?.identity === html.id);
          }

          setTimeout(() => {
            htmlElement.current?.appendChild(html)
          }, 100);;
        });
    }

    return twil.subscribeToTwilioUpdates((e) => {
      if (isLocalUser)
        setIsMuted(!twil.isMicEnabled());
      else if (e?.type == 'UserMuteChanged' && e.userId === html.id)
        setIsMuted(e?.value);

      if (e?.type === 'DominantSpeakerChanged') {
        setIsDominant(!isMuted && e.userId === html.id);
      }
    });
  }, [])

  if (!user && !displayName) return null;

  return (
    <div
      className={cn(
        "relative rounded-lg overflow-hidden bg-overlay-heavy transition-all duration-300 isolate group shadow-lg ring-1 ring-panel-dark-border",
        "w-[120px] aspect-[4/3] sm:w-[160px]",
        isDominant && !isMuted ? "ring-2 ring-green-500 shadow-green-500/20" : ""
      )}
    >
      <div
        ref={htmlElement}
        className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-cover"
      />

      {/* User Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-center justify-between">
        {user ? (
          <UserPopOver user={user} content={
            <div className="flex items-center gap-1.5 max-w-full cursor-pointer hover:bg-background/10 rounded p-0.5 transition-colors">
              <UserAvatar src={user.photoUrl} name={user.name} className="w-5 h-5 ring-1 ring-white/20" fallbackType="text" />
              <span className="text-meta-sm text-on-media-text font-medium truncate drop-shadow-md">{user.name}</span>
            </div>
          } />
        ) : (
          <div className="flex items-center gap-1.5 max-w-full min-w-0 p-0.5">
            <UserAvatar name={displayName} className="w-5 h-5 ring-1 ring-white/20" fallbackType="text" />
            <span className="min-w-0 truncate text-meta-sm text-on-media-text font-medium drop-shadow-md">
              {displayName}
              {subLabel && <span className="ml-1 font-normal opacity-80">{subLabel}</span>}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-overlay-control backdrop-blur-sm shadow-sm ring-1 ring-panel-dark-border">
          {(mutedOverride ?? isMuted) ? <MicOff className="w-3 h-3 text-destructive" /> : <Mic className={cn("w-3 h-3 text-on-media-text", isDominant && "text-green-400")} />}
        </div>
      </div>

      {/* Screen share indicator for local user */}
      {user?.id === My.user.id && twil.isScreenSharing() && (
        <div className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-lg ring-2 ring-black/50" title="Sharing Screen" />
      )}
    </div>
  );
}

// 전체화면 컨트롤 버튼
const ctrlBtn = "flex items-center justify-center w-9 h-9 rounded-md bg-white/10 text-on-media-text hover:bg-white/20 transition-colors disabled:opacity-40 disabled:hover:bg-white/10";
// floating 창 헤더/줌바 버튼 (작음)
const winBtn = "flex items-center justify-center w-7 h-7 rounded-md text-on-media-text hover:bg-white/15 transition-colors disabled:opacity-40 disabled:hover:bg-transparent";
const ZOOM_MIN = 1, ZOOM_MAX = 4;
const MIN_W = 280, MIN_H = 200;
const RECT_KEY = 'nextpod.screenShareWindow.rect';
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// floating 창 기본 위치/크기 (우하단, 16:9 + 헤더)
function defaultWindowRect() {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = clamp(Math.round(vw * 0.38), 360, 640);
  const h = Math.round((w * 9) / 16) + 36;
  return { x: Math.max(16, vw - w - 24), y: Math.max(72, vh - h - 28), w, h };
}
// 저장된 위치/크기 복원 (현재 뷰포트 안으로 클램프)
function loadWindowRect() {
  try {
    const s = localStorage.getItem(RECT_KEY);
    if (s) {
      const r = JSON.parse(s);
      if (r && typeof r.x === 'number' && r.w >= MIN_W && r.h >= MIN_H) {
        const w = Math.min(r.w, window.innerWidth), h = Math.min(r.h, window.innerHeight);
        return { w, h, x: clamp(r.x, 0, Math.max(0, window.innerWidth - w)), y: clamp(r.y, 0, Math.max(0, window.innerHeight - h)) };
      }
    }
  } catch { /* noop */ }
  return defaultWindowRect();
}

// 8방향 리사이즈 핸들 정의 (가장자리 + 모서리)
const RESIZE_HANDLES: { dir: string; cls: string }[] = [
  { dir: 'n', cls: 'top-0 left-3 right-3 h-1.5 cursor-ns-resize' },
  { dir: 's', cls: 'bottom-0 left-3 right-3 h-1.5 cursor-ns-resize' },
  { dir: 'w', cls: 'left-0 top-3 bottom-3 w-1.5 cursor-ew-resize' },
  { dir: 'e', cls: 'right-0 top-3 bottom-3 w-1.5 cursor-ew-resize' },
  { dir: 'nw', cls: 'top-0 left-0 w-3 h-3 cursor-nwse-resize' },
  { dir: 'se', cls: 'bottom-0 right-0 w-3 h-3 cursor-nwse-resize' },
  { dir: 'ne', cls: 'top-0 right-0 w-3 h-3 cursor-nesw-resize' },
  { dir: 'sw', cls: 'bottom-0 left-0 w-3 h-3 cursor-nesw-resize' },
];

/**
 * 원격 화면공유 창. 세 가지 모드:
 * - floating: 드래그 이동 + 8방향 리사이즈 + 내부 zoom/pan (위치·크기 localStorage persist)
 * - fullscreen: 전체 화면(컨트롤 auto-hide) + zoom/pan
 * - hidden: 닫으면 우하단 재열기 pill 표시
 * 모든 모드 portal 렌더로 통화 헤더 패널의 backdrop-filter 함정 회피.
 * zoom: 휠(커서 기준)·드래그 패닝·더블클릭 토글·−/+/리셋. PIP: 웹 Picture-in-Picture.
 */
export const ScreenShareWindow: React.FC<{
  span: HTMLElement;
  sharerId: string;
  /** 발표자 이름을 직접 지정 — 비로그인 게스트(통화 링크)는 users 조회가 불가하므로 */
  sharerNameOverride?: string;
}> = ({ span, sharerId, sharerNameOverride }) => {
  const stageRef = useRef<HTMLDivElement>(null);   // 영상 뷰포트(overflow hidden)
  const fullRef = useRef<HTMLDivElement>(null);    // transform 적용 래퍼(영상 span 보유)
  const [mode, setMode] = useState<'floating' | 'fullscreen'>('floating');
  const [hidden, setHidden] = useState(false);
  const [rect, setRect] = useState(loadWindowRect);
  const [sharerName, setSharerName] = useState<string>();
  const [pipActive, setPipActive] = useState(false);
  // PIP 는 데스크톱 웹에서만 노출 — 모바일 브라우저(ios/android-browser)·네이티브 앱은
  // Picture-in-Picture 지원이 불안정하고 UX 도 부적합하므로 버튼 자체를 숨긴다.
  const pipSupported = UtilApp.getPlatform() === 'web' && typeof document !== 'undefined' && !!(document as any).pictureInPictureEnabled;

  // zoom/pan 상태
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);      // 패닝 드래그
  const [interacting, setInteracting] = useState(false); // 창 이동/리사이즈 중
  const [controlsVisible, setControlsVisible] = useState(true); // 전체화면 컨트롤 auto-hide
  const zoomState = useRef({ scale: 1, tx: 0, ty: 0 });
  const dragRef = useRef({ startX: 0, startY: 0, baseX: 0, baseY: 0, active: false });
  const hideTimer = useRef<number>();
  useEffect(() => { zoomState.current = { scale, tx, ty }; }, [scale, tx, ty]);

  const getVideo = () => span.querySelector('video') as HTMLVideoElement | null;

  function revealControls() {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 2600);
  }

  // 발표자 이름
  useEffect(() => {
    if (sharerNameOverride) setSharerName(sharerNameOverride);
    else if (sharerId === My.user.id) setSharerName(My.user.name);
    else Fire.getUser(sharerId).then(u => setSharerName(u?.name)).catch(() => {});
  }, [sharerId, sharerNameOverride]);

  // 위치/크기 persist
  useEffect(() => { try { localStorage.setItem(RECT_KEY, JSON.stringify(rect)); } catch { /* noop */ } }, [rect]);

  // 뷰포트 리사이즈 시 창을 화면 안으로 클램프
  useEffect(() => {
    const onResize = () => setRect(r => {
      const w = Math.min(r.w, window.innerWidth), h = Math.min(r.h, window.innerHeight);
      return { w, h, x: clamp(r.x, 0, Math.max(0, window.innerWidth - w)), y: clamp(r.y, 0, Math.max(0, window.innerHeight - h)) };
    });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 영상 노드를 현재 스테이지로 이동
  useEffect(() => { if (!hidden) fullRef.current?.appendChild(span); }, [mode, hidden, span]);

  // 모드/표시 전환 시 zoom 리셋
  useEffect(() => { setScale(1); setTx(0); setTy(0); }, [mode, hidden]);

  // 전체화면 Esc → floating 복귀
  useEffect(() => {
    if (mode !== 'fullscreen') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMode('floating'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  // 전체화면 컨트롤 auto-hide
  useEffect(() => {
    if (mode !== 'fullscreen') { window.clearTimeout(hideTimer.current); setControlsVisible(true); return; }
    revealControls();
    return () => window.clearTimeout(hideTimer.current);
  }, [mode]);

  // PIP 상태 동기화 (영상 노드가 모드 전환으로 옮겨질 수 있어 재바인딩)
  useEffect(() => {
    const video = getVideo();
    if (!video) return;
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    video.addEventListener('enterpictureinpicture', onEnter);
    video.addEventListener('leavepictureinpicture', onLeave);
    setPipActive((document as any).pictureInPictureElement === video);
    return () => {
      video.removeEventListener('enterpictureinpicture', onEnter);
      video.removeEventListener('leavepictureinpicture', onLeave);
    };
  }, [span, mode, hidden]);

  // 휠 zoom 은 native non-passive 로 붙여야 preventDefault 가능 (React onWheel 은 passive)
  useEffect(() => {
    const stage = stageRef.current;
    if (hidden || !stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      applyZoom(e.clientX, e.clientY, zoomState.current.scale * factor);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [hidden, mode]);

  // ---- zoom / pan ----
  function clampPan(nx: number, ny: number, s: number) {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return { x: nx, y: ny };
    const maxX = (s - 1) * r.width / 2;
    const maxY = (s - 1) * r.height / 2;
    return { x: clamp(nx, -maxX, maxX), y: clamp(ny, -maxY, maxY) };
  }
  function applyZoom(clientX: number, clientY: number, newScaleRaw: number) {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return;
    const { scale: s, tx: ctx, ty: cty } = zoomState.current;
    const ns = clamp(newScaleRaw, ZOOM_MIN, ZOOM_MAX);
    const cx = clientX - r.left - r.width / 2;
    const cy = clientY - r.top - r.height / 2;
    const nx = ns === 1 ? 0 : cx - (cx - ctx) * (ns / s);
    const ny = ns === 1 ? 0 : cy - (cy - cty) * (ns / s);
    const p = clampPan(nx, ny, ns);
    setScale(ns); setTx(p.x); setTy(p.y);
  }
  function zoomStep(dir: number) {
    const r = stageRef.current?.getBoundingClientRect();
    const cx = r ? r.left + r.width / 2 : 0;
    const cy = r ? r.top + r.height / 2 : 0;
    applyZoom(cx, cy, zoomState.current.scale * (dir > 0 ? 1.3 : 1 / 1.3));
  }
  function resetZoom() { setScale(1); setTx(0); setTy(0); }
  function onDoubleClick(e: React.MouseEvent) {
    if (zoomState.current.scale > 1.01) resetZoom();
    else applyZoom(e.clientX, e.clientY, 2.5);
  }
  function onStagePointerDown(e: React.PointerEvent) {
    if (zoomState.current.scale <= 1.01) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: tx, baseY: ty, active: true };
    setDragging(true);
  }
  function onStagePointerMove(e: React.PointerEvent) {
    if (!dragRef.current.active) return;
    const p = clampPan(
      dragRef.current.baseX + (e.clientX - dragRef.current.startX),
      dragRef.current.baseY + (e.clientY - dragRef.current.startY),
      zoomState.current.scale,
    );
    setTx(p.x); setTy(p.y);
  }
  function onStagePointerUp() { dragRef.current.active = false; setDragging(false); }

  // ---- 창 이동 / 리사이즈 ----
  function startWindowDrag(e: React.PointerEvent) {
    e.preventDefault();
    const start = { px: e.clientX, py: e.clientY, x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    setInteracting(true);
    const onMove = (ev: PointerEvent) => setRect(r => ({
      ...r,
      x: clamp(start.x + (ev.clientX - start.px), 0, window.innerWidth - start.w),
      y: clamp(start.y + (ev.clientY - start.py), 0, window.innerHeight - start.h),
    }));
    const onUp = () => { document.removeEventListener('pointermove', onMove); document.removeEventListener('pointerup', onUp); setInteracting(false); };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }
  function startResize(dir: string, e: React.PointerEvent) {
    e.preventDefault(); e.stopPropagation();
    const start = { px: e.clientX, py: e.clientY, x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    setInteracting(true);
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - start.px, dy = ev.clientY - start.py;
      let { x, y, w, h } = start;
      if (dir.includes('e')) w = clamp(start.w + dx, MIN_W, window.innerWidth - start.x);
      if (dir.includes('s')) h = clamp(start.h + dy, MIN_H, window.innerHeight - start.y);
      if (dir.includes('w')) { w = clamp(start.w - dx, MIN_W, start.x + start.w); x = start.x + start.w - w; }
      if (dir.includes('n')) { h = clamp(start.h - dy, MIN_H, start.y + start.h); y = start.y + start.h - h; }
      setRect({ x, y, w, h });
    };
    const onUp = () => { document.removeEventListener('pointermove', onMove); document.removeEventListener('pointerup', onUp); setInteracting(false); };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  async function togglePip() {
    const video = getVideo();
    if (!video) return;
    try {
      const doc = document as any;
      if (doc.pictureInPictureElement) await doc.exitPictureInPicture();
      else if ((video as any).requestPictureInPicture) await (video as any).requestPictureInPicture();
      else if ((video as any).webkitSetPresentationMode) { (video as any).webkitSetPresentationMode('picture-in-picture'); setPipActive(true); }
    } catch { /* 사용자 취소/미지원 무시 */ }
  }

  // ---- 공통 조각 ----
  const presenterLabel = (
    <div className="flex items-center gap-1.5 min-w-0">
      <MonitorUp className="w-3.5 h-3.5 text-blue-400 shrink-0" />
      <span className="truncate">{sharerName ?? ''}</span>
    </div>
  );
  const pipButton = (cls: string, size: string) => pipSupported && (
    <button type="button" onClick={togglePip} className={cn(cls, pipActive && "text-blue-400 ring-1 ring-blue-400/50")} title={t`Picture-in-picture`} aria-label={t`Picture-in-picture`}>
      <PictureInPicture2 className={size} />
    </button>
  );
  const zoomControls = (btn: string) => (
    <>
      <button type="button" onClick={() => zoomStep(-1)} className={btn} title={t`Zoom out`} aria-label={t`Zoom out`} disabled={scale <= ZOOM_MIN + 0.01}>
        <Minus className="w-4 h-4" />
      </button>
      <span className="text-on-media-text text-meta-sm w-10 text-center tabular-nums">{Math.round(scale * 100)}%</span>
      <button type="button" onClick={() => zoomStep(1)} className={btn} title={t`Zoom in`} aria-label={t`Zoom in`} disabled={scale >= ZOOM_MAX - 0.01}>
        <Plus className="w-4 h-4" />
      </button>
      <button type="button" onClick={resetZoom} className={btn} title={t`Reset zoom`} aria-label={t`Reset zoom`} disabled={scale <= ZOOM_MIN + 0.01}>
        <RotateCcw className="w-4 h-4" />
      </button>
    </>
  );
  // 영상 스테이지 (zoom/pan) — floating·fullscreen 공통
  const stageEl = (extra: string) => (
    <div
      ref={stageRef}
      className={cn("relative overflow-hidden bg-black", extra, scale > 1.01 ? (dragging ? "cursor-grabbing" : "cursor-grab") : "")}
      onDoubleClick={onDoubleClick}
      onPointerDown={onStagePointerDown}
      onPointerMove={onStagePointerMove}
      onPointerUp={onStagePointerUp}
      onPointerLeave={onStagePointerUp}
    >
      <div
        ref={fullRef}
        className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-contain"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: (dragging || interacting) ? 'none' : 'transform 70ms ease-out',
        }}
      />
    </div>
  );

  // ---- 렌더 ----
  if (hidden) {
    return createPortal(
      <button
        type="button"
        onClick={() => setHidden(false)}
        className="fixed bottom-4 right-4 z-[200] flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-full bg-[rgb(var(--panel-dark))] text-[rgb(var(--panel-dark-text))] shadow-lg ring-1 ring-panel-dark-border hover:brightness-110 transition"
        title={t`Show shared screen`}
      >
        <MonitorUp className="w-4 h-4 text-blue-400" />
        <span className="text-meta-sm font-medium truncate max-w-[160px]">{sharerName || t`Show shared screen`}</span>
      </button>,
      document.body,
    );
  }

  if (mode === 'fullscreen') {
    return createPortal(
      <div className="fixed inset-0 z-[200] bg-black flex flex-col select-none" onMouseMove={revealControls}>
        <div className={cn(
          "absolute top-3 left-3 z-10 max-w-[55%] px-2.5 py-1.5 rounded-lg bg-black/55 backdrop-blur-sm text-on-media-text text-sm font-medium transition-opacity duration-300",
          !controlsVisible && "opacity-0 pointer-events-none",
        )}>
          {presenterLabel}
        </div>
        <div
          className={cn(
            "absolute top-3 right-3 z-10 flex items-center gap-1 px-1.5 py-1 rounded-lg bg-black/55 backdrop-blur-sm transition-opacity duration-300",
            !controlsVisible && "opacity-0 pointer-events-none",
          )}
          onMouseEnter={() => { window.clearTimeout(hideTimer.current); setControlsVisible(true); }}
          onMouseLeave={revealControls}
        >
          {zoomControls(ctrlBtn)}
          <div className="w-px h-5 bg-white/15 mx-1" />
          {pipButton(ctrlBtn, "w-5 h-5")}
          <button type="button" onClick={() => setMode('floating')} className={ctrlBtn} title={t`Exit full screen`} aria-label={t`Exit full screen`}>
            <Minimize2 className="w-5 h-5" />
          </button>
        </div>
        {stageEl('flex-1 min-h-0')}
      </div>,
      document.body,
    );
  }

  // floating
  return createPortal(
    <div
      className="fixed z-[200] group flex flex-col rounded-xl overflow-hidden bg-[rgb(var(--panel-dark))] shadow-2xl ring-1 ring-panel-dark-border select-none"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
    >
      {/* 헤더 (드래그 핸들) */}
      <div
        className="flex items-center justify-between gap-2 px-2.5 h-9 shrink-0 bg-black/40 text-on-media-text cursor-move"
        onPointerDown={startWindowDrag}
      >
        <div className="flex items-center gap-1.5 min-w-0 text-meta-sm font-medium">
          <MonitorUp className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <span className="truncate">{sharerName ?? ''}</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0" onPointerDown={(e) => e.stopPropagation()}>
          {pipButton(winBtn, "w-4 h-4")}
          <button type="button" onClick={() => setMode('fullscreen')} className={winBtn} title={t`Full screen`} aria-label={t`Full screen`}>
            <Maximize2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setHidden(true)} className={winBtn} title={t`Close`} aria-label={t`Close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 영상 스테이지 */}
      {stageEl('flex-1 min-h-0')}

      {/* 줌 컨트롤 바 (hover 시 표시) */}
      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-0.5 px-1 py-0.5 rounded-lg bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
        onPointerDown={(e) => e.stopPropagation()}
      >
        {zoomControls(winBtn)}
      </div>

      {/* 리사이즈 핸들 */}
      {RESIZE_HANDLES.map(h => (
        <div key={h.dir} onPointerDown={(e) => startResize(h.dir, e)} className={cn("absolute z-20", h.cls)} />
      ))}
    </div>,
    document.body,
  );
}

export const CallTitleBadge = (roomName: string, nrOfParticipants: number, disabled: boolean) => {
  return (
    <div className='flex items-center gap-3'>
      {roomName && <span className='font-semibold text-lg truncate max-w-[200px]' title={roomName}>{roomName}</span>}
      {MiniCallBadge(nrOfParticipants, disabled)}
    </div>
  );
}

export const MiniCallBadge = (nrOfCallUsers: number, disabled: boolean) => {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "flex items-center gap-1.5 px-2 py-0.5 h-6 shrink-0 transition-opacity",
        disabled ? "bg-muted text-muted-foreground opacity-70" : "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
        "border"
      )}
    >
      <Headphones className="w-3 h-3" />
      <span className="text-xs font-medium tabular-nums">{nrOfCallUsers}</span>
    </Badge>
  );
}